import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Col, Flex, Form, FormProps, Input, Row } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';

// components
import FormElement from 'components/formElement/formElement';
import ComponentHeader from 'components/componentHeader/componentHeader';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';

// services
import { checkPhoneValidation } from 'utils/validators/phone';
import NotificationService from 'core/services/notification';
import OTP from 'components/otp/otp';
import authService from 'core/services/auth';
import SuccessComponent from 'components/success/success';
import { Admin } from 'core/models/admin';
import { cancelOtp, updateProfileData, verifyOtp } from '../services/profileService';
import { resendOtp } from 'modules/main/auth/register/services/contactProfileService';
import { useDispatch, useSelector } from 'react-redux';
import { setAdmin } from 'stores/useAdminStore';
import { RootState } from 'stores/store';

const ProfileDataComponent: FunctionComponent = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const admin = useSelector((state: RootState) => state.admin);

	const [profileForm] = Form.useForm();
	const phone = Form.useWatch('phone_number', profileForm);
	const name = Form.useWatch('name', profileForm);
	const name_en = Form.useWatch('name_en', profileForm);
	const email = Form.useWatch('email', profileForm);

	const [isLoading, setIsLoading] = useState(false);
	const [isOtpLoading, setIsOtpLoading] = useState(false);
	const [otpTtl, setOtpTtl] = useState<number>(60);
	const [isResendLoading, setIsResendLoading] = useState(false);
	const [invalidOtp, setInvalidOtp] = useState(false);

	const [currentStep, setCurrentStep] = useState<'data' | 'otp' | 'success'>('data');

	useEffect(() => {
		updateUserData(admin as Admin);
	}, []);

	type FieldType = {
		name: string;
		name_en: string;
		email: string;
		phone_country_code: string;
		phone_number: string;
	};

	const updateUserData = (admin: Admin) => {
		profileForm.setFieldsValue({
			name: admin?.translated_name.ar,
			name_en: admin?.translated_name.en,
			email: admin?.email,
			phone_number: admin?.phone_number,
			phone_country_code: admin?.phone_country_code,
		});
	};

	const isUnchanged = () => {
		return (
			phone === admin?.phone_number &&
			name === admin?.translated_name.ar &&
			name_en === admin?.translated_name.en &&
			email === admin?.email
		);
	};

	const onChangePhone = ({ number, dialCode }: PhoneInputReturn) => {
		profileForm.setFieldsValue({
			phone_number: number,
			phone_country_code: dialCode,
		});
	};

	const validatePhone = (_: any, value: any) => {
		if (checkPhoneValidation(profileForm.getFieldValue('phone_country_code'),value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.reject();
	};

	const onChangeData: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsLoading(true);

			const response = await updateProfileData(values);

			authService.setData(authService.isMainAdmin, authService.permissions, authService.registrationStep);
			dispatch(setAdmin(response.company_user));

			if (response.pending_phone_update) {
				NotificationService.success(t('COMMON.SUCCESS'), t('PROFILE.OTP_SENT'));
				setOtpTtl(response.otp_ttl || 60);
				setCurrentStep('otp');
			} else {
				NotificationService.success(t('COMMON.SUCCESS'), t('PROFILE.PROFILE_UPDATED'));
			}

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	const onResendOtp = async () => {
		try {
			setIsResendLoading(true);
			await resendOtp('');
			NotificationService.success(t('COMMON.SUCCESS'), t('REGISTER.OTP_RESENT'));
			setIsResendLoading(false);
		} catch (error) {
			setIsResendLoading(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	const onCancelOtp = async () => {
		try {
			const response = await cancelOtp();
			authService.setData(authService.isMainAdmin, authService.permissions, authService.registrationStep);
			dispatch(setAdmin(response.company_user));
			updateUserData(response.company_user);

			setCurrentStep('data');
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	const onOtpFinish = async (otp: string) => {
		try {
			setIsOtpLoading(true);

			const response = await verifyOtp({ otp: otp });

			authService.setData(authService.isMainAdmin, authService.permissions, authService.registrationStep);
			dispatch(setAdmin(response.company_user));

			setIsOtpLoading(false);
			setCurrentStep('success');

			setTimeout(() => {
				setCurrentStep('data');
			}, 4000);
		} catch (error: any) {
			setIsLoading(false);
			setInvalidOtp(true);
		}
	};

	return (
		<Card>
			<Row>
				<Col span={24} lg={8}>
					<ComponentHeader title={t('PROFILE.USER_PROFILE_INF')} subtitle={t('PROFILE.UPDATE_YOUR_PROFILE')} />
				</Col>
				<Col span={24} lg={16}>
					<Flex align="center" gap={20} style={{ minHeight: 320, width: '100%' }}>
						<AnimatePresence mode="wait">
							{currentStep === 'data' ? (
								<motion.div
									transition={{
										duration: 0.3,
									}}
									initial={{ opacity: 0, y: 0 }}
									exit={{ opacity: 0, y: -20 }}
									animate={{ opacity: 1 }}
									style={{ width: '100%' }}
									key="data">
									<Form form={profileForm} name="profileForm" onFinish={onChangeData}>
										<FormElement name="name" type="text" label={t('COMMON.NAME_AR')} />

										<FormElement name="name_en" type="text" label={t('COMMON.NAME_EN')} />

										<FormElement
											name="email"
											type="email"
											label={t('COMMON.EMAIL')}
											rules={[{ type: 'email', message: t('COMMON.EMAIL_INVALID') }]}
										/>

										<Form.Item
											name="phone_number"
											rules={[{ required: true, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
											<PhoneInputComponent
												onChange={onChangePhone}
												userPhoneNumber={admin?.phone_number}
												userDialCode={admin?.phone_country_code}
											/>
										</Form.Item>

										<Form.Item name="phone_country_code" hidden>
											<Input type="hidden" />
										</Form.Item>

										<Form.Item style={{ marginTop: 40 }}>
											<Button block type="primary" htmlType="submit" loading={isLoading} disabled={isUnchanged()}>
												{t('COMMON.UPDATE')}
											</Button>
										</Form.Item>
									</Form>
								</motion.div>
							) : currentStep === 'otp' ? (
								<motion.div
									transition={{
										duration: 0.3,
									}}
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: -20 }}
									style={{ width: '100%', maxWidth: 400 }}
									key="otp">
									<h6 className="text-center mb-0">{t('REGISTER.VERIFY_PHONE')}</h6>

									<p className="text-light text-center">
										{t('REGISTER.ENTER_OTP')}
										<span dir="ltr" style={{ fontSize: 16, color: '#1677ff' }}>
											{profileForm.getFieldValue('phone_number')}
										</span>
									</p>
									<OTP
										isLoading={isOtpLoading}
										onConfirm={onOtpFinish}
										onResend={onResendOtp}
										isResendLoading={isResendLoading}
										ttl={otpTtl}
										style={{ width: 'var(--inputWidth)', maxWidth: '400px' }}
										isInvalid={invalidOtp}
									/>

									<Button variant="link" color="danger" onClick={onCancelOtp} className="m-auto mt-2 d-block">
										{t('COMMON.CANCEL')}
									</Button>
								</motion.div>
							) : (
								<>
									<Flex vertical align="center" justify="center" gap={50}>
										<h6 style={{ color: '#4caf50' }}>{t('PROFILE.NUMBER_CHANGED')}</h6>
										<SuccessComponent />
									</Flex>
								</>
							)}
						</AnimatePresence>
					</Flex>
				</Col>
			</Row>
		</Card>
	);
};

export default ProfileDataComponent;
